import type { LotView, Outcome } from "@hammer/core";
import { formatMoney } from "@hammer/core/money";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { absoluteTime, sourceLabel } from "../components/lot-bits.tsx";
import { useArchived, useLots } from "../lib/queries.ts";
import { useNow } from "../lib/store.ts";

const OUTCOME_LABEL: Record<Outcome, string> = { won: "Won", lost: "Lost", no_sale: "No sale", unknown: "Sold" };

export function History() {
  const lots = useLots().data ?? [];
  const archived = useArchived().data ?? [];
  const now = useNow();
  const [outcome, setOutcome] = useState<Outcome | "all">("all");
  const [tag, setTag] = useState<string | null>(null);

  const closed = useMemo(() => {
    const all = [...lots, ...archived].filter((l) => l.phase === "closed" || l.result);
    return all.sort((a, b) => Date.parse(b.latest?.endAt ?? b.createdAt) - Date.parse(a.latest?.endAt ?? a.createdAt));
  }, [lots, archived]);
  const tags = [...new Set(closed.flatMap((l) => l.tags))].sort();
  const rows = closed.filter((l) => (outcome === "all" || (l.result?.outcome ?? "unknown") === outcome) && (!tag || l.tags.includes(tag)));

  const won = closed.filter((l) => l.result?.outcome === "won");
  const spent = won.reduce((s, l) => s + (l.result?.allInCents ?? l.result?.hammerCents ?? 0), 0);

  return (
    <div className="grid gap-6">
      <h1 className="heading text-[26px]">History</h1>
      <dl className="flex flex-wrap gap-x-10 gap-y-3">
        <Stat label="Lots closed" value={String(closed.length)} />
        <Stat label="You won" value={String(won.length)} />
        <Stat label="Spent, all in" value={formatMoney(spent)} />
      </dl>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="tabs" role="group" aria-label="Outcome">
          {(["all", "won", "lost", "unknown", "no_sale"] as const).map((o) => (
            <button key={o} aria-pressed={outcome === o} onClick={() => setOutcome(o)}>{o === "all" ? "All" : OUTCOME_LABEL[o]}</button>
          ))}
        </div>
        {tags.map((t) => <button key={t} className="chip" aria-pressed={tag === t} onClick={() => setTag(tag === t ? null : t)}>{t}</button>)}
      </div>

      {rows.length ? (
        <div className="overflow-x-auto -mx-1">
          <table className="data min-w-[40rem]">
            <thead>
              <tr><th>Closed</th><th>Lot</th><th>Result</th><th className="r">Sold for</th><th className="r">All in</th><th className="r">Last bid seen</th></tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id}>
                  <td className="muted whitespace-nowrap">{l.latest?.endAt ? absoluteTime(l.latest.endAt, now) : "—"}</td>
                  <td>
                    <Link to="/lot/$id" params={{ id: l.id }} className="underline underline-offset-2">{l.title}</Link>
                    <div className="text-[12.5px] muted">{sourceLabel(l)}{l.archivedAt ? ", archived" : ""}</div>
                  </td>
                  <td className={l.result?.outcome === "won" ? "text-green font-[650]" : ""}>{l.result ? OUTCOME_LABEL[l.result.outcome] : <span className="text-amber">Not recorded</span>}</td>
                  <td className="r num">{formatMoney(l.result?.hammerCents ?? l.latest?.soldPriceCents ?? null)}</td>
                  <td className="r num">{formatMoney(l.result?.allInCents ?? null)}</td>
                  <td className="r num muted">{formatMoney(lastSeenBid(l))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">No closed lots{outcome !== "all" || tag ? " match" : " yet"}. When lots close, their results land here and become your own price guide.</p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[13px] muted">{label}</dt>
      <dd className="board text-[26px] m-0">{value}</dd>
    </div>
  );
}

/** The last bid Hammer observed before the lot closed. */
function lastSeenBid(l: LotView): number | null {
  return l.latest?.currentBidCents ?? null;
}
